import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from 'src/components/ui/Icon/Icon';
import TopBar from 'src/components/ui/TopBar/TopBar';
import Stepper from 'src/components/ui/Stepper/Stepper';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import { $publicApi } from 'src/services/api';
import styles from '../styles/LoginAndRegister.module.scss';
import { useRegistrationFormContext } from './RegistrationFormContext';
import RegistrationLockedView from './RegistrationLockedView';
import CompanyDataStep from './steps/CompanyDataStep';
import VerificationStep from './steps/VerificationStep';
import BankAndTermsStep from './steps/BankAndTermsStep';
import ConfirmStep from './steps/ConfirmStep';
import { RegistrationStatus } from './schema';

interface RegisterWizardProps {
  registrationKey: string;
}

const RegisterWizard: React.FC<RegisterWizardProps> = ({ registrationKey }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hydrate } = useRegistrationFormContext();
  const [step, setStep] = useState(1);
  const [maxVisitedStep, setMaxVisitedStep] = useState(1);

  const registrationQuery = $publicApi.useQuery('get', '/registrations/{registration_key}', {
    params: { path: { registration_key: registrationKey } },
  });

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (registrationQuery.data && !hydratedRef.current) {
      hydrate(registrationQuery.data);
      hydratedRef.current = true;
    }
  }, [registrationQuery.data, hydrate]);

  const advance = () => {
    setStep(prev => {
      const next = Math.min(prev + 1, 4);
      setMaxVisitedStep(visited => Math.max(visited, next));
      return next;
    });
  };

  const back = () => setStep(prev => Math.max(prev - 1, 1));

  const goToStep = (target: number) => {
    if (target <= maxVisitedStep) setStep(target);
  };

  const stepLabels = [
    t('registration.steps.company_data_short'),
    t('registration.steps.verification_short'),
    t('registration.steps.bank_and_terms_short'),
    t('registration.steps.confirm_short'),
  ];

  if (registrationQuery.isLoading) {
    return (
      <div className={classNames(styles.root)}>
        <div className={styles.topbar}>
          <TopBar onClick={() => navigate('/login')} />
        </div>
        <div className={styles.main}>
          <div className="w-full p-32">
            <LoadingSkeleton numberOfItems={6} />
          </div>
        </div>
      </div>
    );
  }

  const { data } = registrationQuery;
  if (data && data.status && data.status !== 'draft') {
    return <RegistrationLockedView status={data.status as RegistrationStatus} />;
  }

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.topbar}>
        <TopBar onClick={() => navigate('/login')} />
      </div>
      <div className={styles.main}>
        <div className="w-full flex justify-center p-20 md:p-32 overflow-auto">
          <div className="w-full max-w-[80rem] flex flex-col gap-24">
            <div className="mb-8">
              <h2 className="typo-title-36 text-full mb-12">{t('registration.wizard_title')}</h2>
              <span className="typo-title-24 text-faded">{t('registration.wizard_copy')}</span>
            </div>

            <div className="px-8">
              <Stepper currentStep={step} totalSteps={4} stepLabels={stepLabels} />
            </div>

            <div className="mt-16">
              {step === 1 && <CompanyDataStep registrationKey={registrationKey} onAdvance={advance} />}
              {step === 2 && <VerificationStep registrationKey={registrationKey} onAdvance={advance} onBack={back} />}
              {step === 3 && <BankAndTermsStep registrationKey={registrationKey} onAdvance={advance} onBack={back} />}
              {step === 4 && <ConfirmStep registrationKey={registrationKey} onBack={back} goToStep={goToStep} />}
            </div>

            <span className="w-full inline-block typo-text-14 text-faded text-center pt-32">
              {new Date().getFullYear()} {t('general.all_rights_reserved')}
            </span>
          </div>
        </div>
        <div className={classNames(styles.image, 'hidden md:flex md:w-1/3 justify-center items-center')}>
          <div className={styles.hero} aria-hidden />
          <Icon className={styles.icon} name="logo-full-white" width={91} height={30} />
        </div>
      </div>
    </div>
  );
};

export default RegisterWizard;
